/**
 * Invariant Lifecycle Governance demo navigation registry.
 *
 * Single source of truth for the sidebar entries, their accent colors,
 * and the panel components they mount. The order parallels the
 * monograph spine: thesis → fibration → apex stack → seams/bridges →
 * interactive validator → composition lab → attack atlas → theorems →
 * rulebook browser → frozen vectors → cross-reference map → promotion
 * gates → boundary.
 *
 * Panels are loaded lazily so the initial bundle stays focused on the
 * shell + thesis panel; subsequent panels arrive on demand as the user
 * navigates.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import { defineMonographNavigation } from "@csk/demo-ui/components/monograph-demo-shell";
import { DEMO_COLOR, lazyNamedPanels } from "@csk/demo-ui";
import { OverviewPanel } from "../panels/OverviewPanel.js";

const {
  FibrationPanel,
  ApexStackPanel,
  SeamsAndBridgesPanel,
  WitnessValidatorPanel,
  InvariantRelayPanel,
  CompositionLabPanel,
  AttackAtlasPanel,
  TheoremsPanel,
  RulebookBrowserPanel,
  TestVectorsPanel,
  CrossMapPanel,
  LifecycleHistoryPanel,
  PromotionGatesPanel,
  BoundaryPanel,
  ManuscriptPanel,
} = lazyNamedPanels({
  FibrationPanel: async () => import("../panels/FibrationPanel.js"),
  ApexStackPanel: async () => import("../panels/ApexStackPanel.js"),
  SeamsAndBridgesPanel: async () => import("../panels/SeamsAndBridgesPanel.js"),
  WitnessValidatorPanel: async () =>
    import("../panels/WitnessValidatorPanel.js"),
  InvariantRelayPanel: async () => import("../panels/InvariantRelayPanel.js"),
  CompositionLabPanel: async () => import("../panels/CompositionLabPanel.js"),
  AttackAtlasPanel: async () => import("../panels/AttackAtlasPanel.js"),
  TheoremsPanel: async () => import("../panels/TheoremsPanel.js"),
  RulebookBrowserPanel: async () => import("../panels/RulebookBrowserPanel.js"),
  TestVectorsPanel: async () => import("../panels/TestVectorsPanel.js"),
  CrossMapPanel: async () => import("../panels/CrossMapPanel.js"),
  LifecycleHistoryPanel: async () =>
    import("../panels/LifecycleHistoryPanel.js"),
  PromotionGatesPanel: async () => import("../panels/PromotionGatesPanel.js"),
  BoundaryPanel: async () => import("../panels/BoundaryPanel.js"),
  ManuscriptPanel: async () => import("../panels/ManuscriptPanel.js"),
});

export const NAV = [
  {
    id: "overview",
    label: "The Thesis",
    subtitle: "Equivalence does not authorize",
    icon: "◇",
    accent: DEMO_COLOR.tealSoft,
    component: OverviewPanel,
  },
  {
    id: "fibration",
    label: "The Fibration",
    subtitle: "π_I : 𝓔_I → 𝓥 · governed lifts",
    icon: "↘",
    accent: DEMO_COLOR.tealSoft,
    component: FibrationPanel,
  },
  {
    id: "apex",
    label: "Apex Stack",
    subtitle: "Seven persistence conditions",
    icon: "▲",
    accent: DEMO_COLOR.tealSoft,
    component: ApexStackPanel,
  },
  {
    id: "seams",
    label: "Seams & Bridges",
    subtitle: "Sᴬ Sᴿ Sᵁ Sᴹ · controlled bridge discipline",
    icon: "⊏",
    accent: DEMO_COLOR.danger,
    component: SeamsAndBridgesPanel,
  },
  {
    id: "validator",
    label: "Witness Validator",
    subtitle: "Build a witness · watch the verdict",
    icon: "λ",
    accent: DEMO_COLOR.success,
    component: WitnessValidatorPanel,
  },
  {
    id: "invariant-relay",
    label: "Invariant Relay",
    subtitle: "Mini-game · carry proof through lifecycle gates",
    icon: "▶",
    accent: DEMO_COLOR.tealSoft,
    component: InvariantRelayPanel,
  },
  {
    id: "compose",
    label: "Composition Lab",
    subtitle: "χ₂ ∘ χ₁ · T3 in motion",
    icon: "∘",
    accent: DEMO_COLOR.sky,
    component: CompositionLabPanel,
  },
  {
    id: "attacks",
    label: "Attack Atlas",
    subtitle: "A1..A9 · named composites",
    icon: "✸",
    accent: DEMO_COLOR.pink,
    component: AttackAtlasPanel,
  },
  {
    id: "theorems",
    label: "Theorems & Lemmas",
    subtitle: "T1..T3 · L1..L5",
    icon: "Σ",
    accent: DEMO_COLOR.violet,
    component: TheoremsPanel,
  },
  {
    id: "rulebook",
    label: "Rulebook Browser",
    subtitle: "29 codes · 6 families",
    icon: "§",
    accent: DEMO_COLOR.amber,
    component: RulebookBrowserPanel,
  },
  {
    id: "vectors",
    label: "Test Vectors",
    subtitle: "Frozen vectors · live replay",
    icon: "▦",
    accent: DEMO_COLOR.gold,
    component: TestVectorsPanel,
  },
  {
    id: "crossmap",
    label: "Cross-Reference Map",
    subtitle: "Codes ↔ apex · theorems · attacks · seams",
    icon: "⊞",
    accent: DEMO_COLOR.violet,
    component: CrossMapPanel,
  },
  {
    id: "history",
    label: "Lifecycle History",
    subtitle: "Paginated Phase-8 receipts · approvers · phase views",
    icon: "▤",
    accent: DEMO_COLOR.sky,
    component: LifecycleHistoryPanel,
  },
  {
    id: "gates",
    label: "Promotion Gates",
    subtitle: "G0..G3 · stabilization rule",
    icon: "⟹",
    accent: DEMO_COLOR.sky,
    component: PromotionGatesPanel,
  },
  {
    id: "boundary",
    label: "Boundary",
    subtitle: "Non-claims · manuscript map",
    icon: "⊢",
    accent: DEMO_COLOR.mutedSteel,
    component: BoundaryPanel,
  },
  {
    id: "manuscript",
    label: "Manuscript",
    subtitle: "Monograph · chapter-by-chapter",
    icon: "✎",
    accent: DEMO_COLOR.gold,
    component: ManuscriptPanel,
  },
] as const;

export const NAVIGATION = defineMonographNavigation(NAV);
